import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  OperatingGasRoute,
  OperatingGasRouteGridify,
  OperatingGasRouteRequestPayload,
} from './types';

export const operatingGasRoutesApi = createApi({
  reducerPath: 'operatingGasRoutesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/operating-gas-routes`,
  }),
  tagTypes: ['OperatingGasRoutes', 'OperatingGasRoute'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getOperatingGasRoutes: builder.query<
      OperatingGasRouteGridify[],
      FiltersGrid | void
    >({
      query: params => ({
        url: `/query`,
        params,
      }),
      providesTags: () => ['OperatingGasRoutes'],
    }),
    getOperatingGasRoutesI: builder.infiniteQuery<
      OperatingGasRouteGridify[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['OperatingGasRoutes'],
    }),
    getOperatingGasRouteById: builder.query<OperatingGasRoute, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: 'OperatingGasRoute', id }],
    }),
    createOperatingGasRoute: builder.mutation<
      OperatingGasRoute,
      OperatingGasRouteRequestPayload
    >({
      query: data => ({
        url: ``,
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'OperatingGasRoutes',
        { type: 'OperatingGasRoute', id: result?.id },
      ],
    }),
    updateOperatingGasRoute: builder.mutation<
      OperatingGasRoute,
      OperatingGasRouteRequestPayload
    >({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'OperatingGasRoutes',
        { type: 'OperatingGasRoute', id: result?.id },
      ],
    }),
    deleteOperatingGasRoute: builder.mutation<OperatingGasRoute, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'OperatingGasRoutes',
        { type: 'OperatingGasRoute', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetOperatingGasRoutesQuery,
  useLazyGetOperatingGasRoutesQuery,
  useGetOperatingGasRouteByIdQuery,
  useCreateOperatingGasRouteMutation,
  useUpdateOperatingGasRouteMutation,
  useDeleteOperatingGasRouteMutation,
} = operatingGasRoutesApi;

export const useGetOperatingGasRoutesInfiniteQuery = normalizeInfiniteQueryData(
  operatingGasRoutesApi.useGetOperatingGasRoutesIInfiniteQuery,
);

const getCachedOperatingGasRouteByIdSelector = cachedItemByIdSelector(
  'operatingGasRoutesApi',
  'getOperatingGasRoutesI',
);

export const useGetCachedOperatingGasRouteByIdSelector = id =>
  useSelector(getCachedOperatingGasRouteByIdSelector(id));
